import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

import { WorkspaceService } from '@sa/shared/services/workspace.service';

/**
 * Guard to check if the user has access to tenant-specific routes.
 * Redirects to '/no-auth' if no tenants are available.
 */
@Injectable({ providedIn: 'root' })
export class TenantGuard implements CanActivate {
  private readonly workspaceService: WorkspaceService =
    inject(WorkspaceService);
  private readonly router: Router = inject(Router);
  private readonly msalService: MsalService = inject(MsalService);

  /**
   * Determines if the route can be activated based on tenant availability.
   * @returns Observable emitting true if tenants exist, otherwise a UrlTree redirecting to '/no-auth'.
   */
  public canActivate(): Observable<boolean | UrlTree> {
    if (this.msalService.instance.getActiveAccount()) {
      return this.workspaceService.getClients().pipe(
        map(tenants =>
          Array.isArray(tenants) && tenants.length > 0
            ? true
            : this.router.parseUrl('/no-auth')
        ),
        catchError(() => {
          return of(this.router.parseUrl('/no-auth'));
        })
      );
    } else {
      return of(this.router.parseUrl('/login')).pipe(
        catchError(() => of(this.router.parseUrl('/no-auth')))
      );
    }
  }
}
